import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  fetchConsoleSessionPolicy,
  keepSessionAlive,
  recordSessionActivity,
  type ConsoleSessionPolicy,
} from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';

const REFRESH_INTERVAL_MS = 14 * 60 * 1000;
const ACTIVITY_PING_MS = 60_000;
const WARNING_MS = 2 * 60 * 1000;
const TICK_MS = 5_000;
const DEFAULT_IDLE_MINUTES = 15;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'mousemove'] as const;

const DEFAULT_POLICY: ConsoleSessionPolicy = {
  rememberMeEnabled: true,
  idleTimeoutMinutes: DEFAULT_IDLE_MINUTES,
};

export const CONSOLE_SESSION_QUERY_KEY = ['auth', 'console-session'] as const;

export const useConsoleSessionPolicy = (enabled = true) =>
  useQuery({
    queryKey: CONSOLE_SESSION_QUERY_KEY,
    queryFn: fetchConsoleSessionPolicy,
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    enabled,
  });

/**
 * Platform admin has no Keep me signed in. Unused consoles sign out after the
 * platform idle limit; using the page keeps the person signed in.
 */
export const useSessionKeepAlive = (): {
  warningOpen: boolean;
  staySignedIn: () => void;
  signOutNow: () => void;
} => {
  const authenticated = useAuthStore((state) => state.status === 'authenticated');
  const signOut = useAuthStore((state) => state.signOut);
  const policyQuery = useConsoleSessionPolicy(authenticated);
  const policy = policyQuery.data ?? DEFAULT_POLICY;
  const timeoutMs = policy.idleTimeoutMinutes * 60_000;

  const lastActivityAt = useRef(Date.now());
  const lastPingAt = useRef(0);
  const [warningOpen, setWarningOpen] = useState(false);

  const ping = useCallback(() => {
    lastPingAt.current = Date.now();
    void recordSessionActivity().catch(() => undefined);
  }, []);

  const markActivity = useCallback(
    (shouldPing: boolean) => {
      lastActivityAt.current = Date.now();
      setWarningOpen(false);
      if (shouldPing && Date.now() - lastPingAt.current >= ACTIVITY_PING_MS) {
        ping();
      }
    },
    [ping],
  );

  const staySignedIn = useCallback(() => {
    lastActivityAt.current = Date.now();
    setWarningOpen(false);
    ping();
    void keepSessionAlive().catch(() => undefined);
  }, [ping]);

  const signOutNow = useCallback(() => {
    setWarningOpen(false);
    void signOut();
  }, [signOut]);

  useEffect(() => {
    if (!authenticated) {
      setWarningOpen(false);
      return;
    }

    lastActivityAt.current = Date.now();
    lastPingAt.current = 0;
    ping();

    const onActivity = () => markActivity(true);
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }

    const renewIfActive = () => {
      if (Date.now() - lastActivityAt.current >= timeoutMs) return;
      void keepSessionAlive().catch(() => undefined);
    };

    const refreshId = globalThis.setInterval(renewIfActive, REFRESH_INTERVAL_MS);

    const tickId = globalThis.setInterval(() => {
      const idleFor = Date.now() - lastActivityAt.current;
      if (idleFor >= timeoutMs) {
        setWarningOpen(false);
        void signOut();
        return;
      }
      setWarningOpen(timeoutMs - idleFor <= WARNING_MS);
    }, TICK_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (Date.now() - lastActivityAt.current >= timeoutMs) {
          void signOut();
          return;
        }
        markActivity(true);
        renewIfActive();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      globalThis.clearInterval(refreshId);
      globalThis.clearInterval(tickId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
    };
  }, [authenticated, timeoutMs, markActivity, ping, signOut]);

  return { warningOpen: authenticated && warningOpen, staySignedIn, signOutNow };
};
